export function vec3(value = {}) {
  return {
    x: Number(value.x || 0),
    y: Number(value.y || 0),
    z: Number(value.z || 0),
  };
}

function dot(a, b) {
  return (
    Number(a.x || 0) * Number(b.x || 0) +
    Number(a.y || 0) * Number(b.y || 0) +
    Number(a.z || 0) * Number(b.z || 0)
  );
}

function sub(a, b) {
  return {
    x: Number(a.x || 0) - Number(b.x || 0),
    y: Number(a.y || 0) - Number(b.y || 0),
    z: Number(a.z || 0) - Number(b.z || 0),
  };
}

function normalize2D(x, z) {
  const len = Math.sqrt(x * x + z * z) || 1;

  return {
    x: x / len,
    z: z / len,
  };
}

export function getViewAxes(view) {
  const type = view?.type || "front";

  if (type === "top") {
    return {
      u: { x: -1, y: 0, z: 0 },
      v: { x: 0, y: 0, z: -1 },
      n: { x: 0, y: 1, z: 0 },
      invertV: false,
    };
  }

  if (type === "right") {
    return {
      u: { x: 0, y: 0, z: 1 },
      v: { x: 0, y: 1, z: 0 },
      n: { x: 1, y: 0, z: 0 },
      invertV: true,
    };
  }

  if (type === "left") {
    return {
      u: { x: 0, y: 0, z: -1 },
      v: { x: 0, y: 1, z: 0 },
      n: { x: -1, y: 0, z: 0 },
      invertV: true,
    };
  }

  if (type === "back") {
    return {
      u: { x: -1, y: 0, z: 0 },
      v: { x: 0, y: 1, z: 0 },
      n: { x: 0, y: 0, z: -1 },
      invertV: true,
    };
  }

  if (type === "section") {
  const frame = getSectionFrame2D(view?.sectionPlane || {});
  const line = frame.line;
  const cameraNormal = frame.cameraNormal;

  return {
    u: { x: -line.x, y: 0, z: -line.z },
    v: { x: 0, y: 1, z: 0 },
    n: { x: cameraNormal.x, y: 0, z: cameraNormal.z },
    invertV: true,
  };
}

  return {
    u: { x: 1, y: 0, z: 0 },
    v: { x: 0, y: 1, z: 0 },
    n: { x: 0, y: 0, z: 1 },
    invertV: true,
  };
}

export function getViewportSizeMm(viewport = {}) {
  return {
    widthMm: Number(viewport.widthMm ?? viewport.width ?? 100),
    heightMm: Number(viewport.heightMm ?? viewport.height ?? 80),
  };
}

export function getViewOriginWorld(view) {
  return vec3(
    view?.projection?.originWorld ||
      view?.camera?.target ||
      { x: 0, y: 0, z: 0 }
  );
}

export function projectWorldToViewportMm(point, view, viewport) {
  const p = vec3(point);
  const origin = getViewOriginWorld(view);
  const axes = getViewAxes(view);
  const size = getViewportSizeMm(viewport);
  const scale = Number(view?.scale || 20);

  const d = sub(p, origin);

  const uWorld = dot(d, axes.u);
  const vWorld = dot(d, axes.v);

  return {
    x: size.widthMm / 2 + uWorld / scale,
    y: axes.invertV
      ? size.heightMm / 2 - vWorld / scale
      : size.heightMm / 2 + vWorld / scale,
  };
}

export function unprojectViewportMmToWorld(pointMm, view, viewport) {
  const size = getViewportSizeMm(viewport);
  const origin = getViewOriginWorld(view);
  const axes = getViewAxes(view);
  const scale = Number(view?.scale || 20);

  const uWorld = (Number(pointMm.x || 0) - size.widthMm / 2) * scale;

  const vPaper = Number(pointMm.y || 0) - size.heightMm / 2;
  const vWorld = axes.invertV ? -vPaper * scale : vPaper * scale;

  return {
    x: origin.x + axes.u.x * uWorld + axes.v.x * vWorld,
    y: origin.y + axes.u.y * uWorld + axes.v.y * vWorld,
    z: origin.z + axes.u.z * uWorld + axes.v.z * vWorld,
  };
}

export function getSectionFrame2D(sectionPlane = {}) {
  const start = sectionPlane?.start || {
    x: -500,
    z: Number(sectionPlane?.value || 0),
  };

  const end = sectionPlane?.end || {
    x: 500,
    z: Number(sectionPlane?.value || 0),
  };

  const sx = Number(start.x || 0);
  const sz = Number(start.z ?? sectionPlane?.value ?? 0);
  const ex = Number(end.x || 0);
  const ez = Number(end.z ?? sectionPlane?.value ?? 0);

  const lx = ex - sx;
  const lz = ez - sz;

  const len = Math.sqrt(lx * lx + lz * lz) || 1;

  const line = {
    x: lx / len,
    z: lz / len,
  };

  // FRONT smer dubine.
  // Ovo je obrnuto od prethodne varijante.
  let depth = {
    x: -lz / len,
    z: lx / len,
  };

  const direction = sectionPlane?.direction || "front";
  const isBack = direction === "back";

  if (isBack) {
    depth = {
      x: -depth.x,
      z: -depth.z,
    };
  }

  return {
    line,
    depth,
    cameraNormal: {
      x: -depth.x,
      z: -depth.z,
    },
    isBack,
    direction,
    start: { x: sx, z: sz },
    end: { x: ex, z: ez },
  };
}